import React, {useCallback, useContext, useEffect} from 'react'
import RSelect from 'react-select'
import SuspenseLoading from '@components/SuspenseLoading'
import {getTimeValue} from '@helpers/websites/scheduleHelpers'
import Select from '@packages/justo-parts/lib/components/fields/Select'
import {CheckoutContext} from '@page-components/Checkout/Checkout/CheckoutContext'
import isEqual from 'lodash/isEqual'
import {Field, Form} from 'simple-react-form'
import usePrevious from 'use-previous'

import HourField from './HourField'
import useTimeField, {deliveryTypeIsField} from './useTimeField'

import styles from './styles.module.css'

export default function TimeField(props) {
  const {save} = props
  const preferences = props.preferences || {}
  const {store} = preferences
  const {timeOptions, setDeliveryType: setDeliveryTypeCtx} = useContext(CheckoutContext)
  const previousTimeOptions = usePrevious(timeOptions)
  const statesTimeNull = ['undefined', 'null', 'now']

  const {
    time,
    setTime,
    deliveryOptions,
    deliveryType,
    setDeliveryType,
    updateTimePreference,
    DELIVERY_VALUES_ENUM
  } = useTimeField({preferences, save})

  const timeInvalid = () => {
    return !time || statesTimeNull.some(timeNull => time.includes(timeNull))
  }

  useEffect(() => {
    if (
      deliveryOptions.length === 1 &&
      (Array.isArray(deliveryType) ||
        (deliveryType && deliveryType?.value !== deliveryOptions[0].value))
    ) {
      onDeliveryTypeChange(deliveryOptions[0])
      if (
        timeInvalid() &&
        (deliveryOptions[0].value as any) === DELIVERY_VALUES_ENUM.schedule_order
      ) {
        onFormChange({days: store.availableScheduleDaysOptions[0].value})
      }
    }
  }, [deliveryOptions.length, store])

  const updateSelectedOption = useCallback(() => {
    const {days: currentDay} = getTimeValue(time)
    const enabledTimeOption = Array.isArray(timeOptions)
      ? timeOptions.find(option => !option.disabled)
      : null
    if (
      enabledTimeOption &&
      deliveryTypeIsField(deliveryType) &&
      (deliveryType?.value as any) === DELIVERY_VALUES_ENUM.schedule_order
    ) {
      onFormChange({days: currentDay, time: enabledTimeOption.value})
      return enabledTimeOption
    }
    return null
  }, [deliveryOptions.length, timeOptions])

  useEffect(() => {
    if (!isEqual(timeOptions, previousTimeOptions)) {
      updateSelectedOption()
    }
  }, [deliveryOptions.length, timeOptions, updateSelectedOption])

  useEffect(() => {
    if (!timeInvalid() && deliveryOptions.length > 1) {
      onDeliveryTypeChange(deliveryOptions[1])
    }
  }, [])

  if (!store) return null

  const onDeliveryTypeChange = newDeliveryType => {
    setDeliveryType(newDeliveryType)
    setDeliveryTypeCtx(newDeliveryType?.value)

    // if the new value changed to deliver now, update backend.
    if (newDeliveryType?.value === DELIVERY_VALUES_ENUM.deliver_now && time !== 'now') {
      updateTimePreference('now')
      setTime(null)
    }
  }

  const onFormChange = ({days: newDay, time: newHourRange}: {days: string; time?: string}) => {
    const {days: currentDay, time: currentHourRange} = getTimeValue(time)

    if (newDay === currentDay && newHourRange === currentHourRange) return

    // if the day changes or is null, set hour range to null.
    if (!newDay) {
      newHourRange = null
    }
    if (newDay !== currentDay) {
      const option = updateSelectedOption()
      newHourRange = option ? option.value : null
    }

    const newTime = `${newHourRange}+${newDay}`
    setTime(newTime)

    // only update backend if day and hour range are non-null.
    newHourRange && newDay && updateTimePreference(newTime)
  }

  return (
    <div>
      {deliveryOptions.length > 0 ? (
        <RSelect
          {...props.selectorProps}
          className={styles.select}
          placeholder="Tipo de entrega"
          name="deliveryType"
          classNamePrefix="orion-select"
          value={deliveryType}
          defaultValue={deliveryType}
          options={deliveryOptions}
          onChange={onDeliveryTypeChange}
          isSearchable={false}
        />
      ) : (
        <p className={styles.noScheduling}>No estamos recibiendo pedidos</p>
      )}

      {deliveryTypeIsField(deliveryType) &&
        (deliveryType?.value as any) === DELIVERY_VALUES_ENUM.schedule_order &&
        (!store.availableScheduleDaysOptions.length ? (
          <p className={styles.noScheduling}>No estamos recibiendo pedidos</p>
        ) : (
          <Form state={getTimeValue(time)} onChange={onFormChange} useFormTag={false}>
            <Field
              className={styles.select}
              fieldName="days"
              placeholder="Selecciona el día"
              type={Select}
              passProps={{...props.selectorProps}}
              options={store.availableScheduleDaysOptions}
            />

            <SuspenseLoading height={40}>
              <HourField
                selectorProps={props.selectorProps}
                days={getTimeValue(time).days}
                store={store}
              />
            </SuspenseLoading>
          </Form>
        ))}
    </div>
  )
}
